'use client';

import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import { MAIN_CONTACTS } from '@/lib/contacts';

function triggerHaptic() {
  try { if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(20); } catch { /* ignore */ }
}

export default function FamilyContacts() {
  const router = useRouter();
  const [callLoadingId, setCallLoadingId] = useState<string | null>(null);
  const [msgLoadingId, setMsgLoadingId] = useState<string | null>(null);
  const [seeAllPressed, setSeeAllPressed] = useState(false);

  const handleCall = useCallback((contactId: string) => {
    if (callLoadingId || msgLoadingId) return;
    triggerHaptic();
    setCallLoadingId(contactId);
    setTimeout(() => {
      router.push(`/call-confirmation-screen?contact=${contactId}`);
    }, 200);
  }, [callLoadingId, msgLoadingId, router]);

  const handleMessage = useCallback((contactId: string) => {
    if (callLoadingId || msgLoadingId) return;
    triggerHaptic();
    setMsgLoadingId(contactId);
    setTimeout(() => {
      router.push(`/messages-and-photos-screen?contact=${contactId}`);
    }, 180);
  }, [callLoadingId, msgLoadingId, router]);

  const handleSeeAll = useCallback(() => {
    if (seeAllPressed) return;
    triggerHaptic();
    setSeeAllPressed(true);
    setTimeout(() => {
      router.push('/mes-proches');
    }, 180);
  }, [seeAllPressed, router]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'clamp(6px, 0.8vh, 10px)', minHeight: 0 }}>
      {/* Section title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--navy)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span style={{ fontSize: 'clamp(18px, 1.8vw, 22px)', fontWeight: 700, color: 'var(--navy)', flex: 1 }}>
          Mes proches
        </span>
        <button
          onClick={handleSeeAll}
          aria-label="Voir tous mes proches"
          disabled={seeAllPressed}
          className="interactive-btn"
          style={{
            background: seeAllPressed ? '#EFF6FF' : 'transparent',
            border: '1.5px solid var(--border)',
            borderRadius: '10px',
            padding: '5px 12px',
            fontSize: 'clamp(14px, 1.5vw, 17px)',
            fontWeight: 600,
            color: 'var(--muted-foreground)',
            cursor: seeAllPressed ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            whiteSpace: 'nowrap',
            minHeight: '40px',
          }}
        >
          {seeAllPressed ? 'Ouverture…' : 'Voir tous'}
          {!seeAllPressed && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          )}
        </button>
      </div>

      {/* Contact cards */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateRows: 'repeat(3, minmax(0, 1fr))',
        gap: 'clamp(6px, 0.8vh, 8px)',
        minHeight: 0,
      }}>
        {MAIN_CONTACTS?.map((contact) => {
          const isCallLoading = callLoadingId === contact?.id;
          const isMsgLoading = msgLoadingId === contact?.id;
          const isAnyLoading = !!(callLoadingId || msgLoadingId);
          const isDisabled = isAnyLoading && !isCallLoading && !isMsgLoading;

          return (
            <div
              key={contact?.id}
              aria-label={`Carte de ${contact?.firstName}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(8px, 1vw, 14px)',
                padding: 'clamp(6px, 0.8vh, 10px) clamp(10px, 1vw, 14px)',
                border: (isCallLoading || isMsgLoading) ? '2px solid #5DBB1F' : '2px solid var(--border)',
                background: (isCallLoading || isMsgLoading) ? '#F0FBE8' : 'var(--card)',
                borderRadius: '16px',
                minHeight: 0,
                width: '100%',
                boxShadow: (isCallLoading || isMsgLoading) ? '0 2px 10px rgba(93,187,31,0.15)' : '0 2px 10px rgba(26,39,68,0.06)',
                overflow: 'hidden',
                opacity: isDisabled ? 0.6 : 1,
              }}>

              {/* Photo */}
              <div style={{
                width: 'clamp(72px, 8vw, 110px)',
                height: 'clamp(72px, 8vw, 110px)',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '3px solid var(--green-primary)',
                flexShrink: 0,
              }}>
                <AppImage
                  src={contact?.photo}
                  alt={contact?.photoAlt}
                  width={110}
                  height={110}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>

              {/* Name + relation */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
                <div style={{ fontSize: 'clamp(20px, 2.2vw, 28px)', fontWeight: 800, color: 'var(--navy)', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {contact?.firstName}
                </div>
                <div style={{ fontSize: 'clamp(16px, 1.7vw, 22px)', fontWeight: 600, color: 'var(--muted-foreground)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {contact?.relationship}
                </div>
                {contact?.info && (
                  <div style={{ fontSize: 'clamp(13px, 1.3vw, 17px)', fontWeight: 500, color: '#5DBB1F', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {contact?.info}
                  </div>
                )}
              </div>

              {/* Action buttons: Call + Message */}
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                flexShrink: 0,
                alignItems: 'center',
              }}>
                {/* Call button */}
                <button
                  onClick={() => handleCall(contact?.id)}
                  aria-label={isCallLoading ? `Appel de ${contact?.firstName} en préparation…` : `Appeler ${contact?.firstName} — ${contact?.relationship}`}
                  aria-busy={isCallLoading}
                  disabled={isDisabled || isMsgLoading}
                  className="interactive-btn"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    background: isCallLoading
                      ? 'linear-gradient(135deg, #3D9A10 0%, #2D7A08 100%)'
                      : 'linear-gradient(135deg, #5DBB1F 0%, #3D9A10 100%)',
                    borderRadius: '12px',
                    padding: 'clamp(8px, 1vh, 14px) clamp(10px, 1.2vw, 16px)',
                    minWidth: 'clamp(80px, 8vw, 100px)',
                    minHeight: '64px',
                    boxShadow: isCallLoading ? 'none' : '0 3px 12px rgba(93,187,31,0.35)',
                    transition: 'all 0.15s ease',
                    border: 'none',
                    cursor: (isDisabled || isMsgLoading) ? 'not-allowed' : 'pointer',
                    opacity: (isDisabled || isMsgLoading) ? 0.6 : 1,
                  }}>
                  {isCallLoading ? (
                    <>
                      <div style={{
                        width: '22px', height: '22px', borderRadius: '50%',
                        border: '3px solid rgba(255,255,255,0.8)',
                        borderTopColor: 'transparent',
                        animation: 'sab-spin 0.7s linear infinite',
                      }} aria-hidden="true" />
                      <span style={{ fontSize: 'clamp(12px, 1.2vw, 14px)', fontWeight: 700, color: 'white', whiteSpace: 'nowrap', marginTop: '2px' }}>
                        Appel…
                      </span>
                    </>
                  ) : (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      <span style={{ fontSize: 'clamp(14px, 1.4vw, 17px)', fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
                        Appeler
                      </span>
                    </>
                  )}
                </button>

                {/* Message button */}
                <button
                  onClick={() => handleMessage(contact?.id)}
                  aria-label={isMsgLoading ? `Ouverture des messages de ${contact?.firstName}…` : `Envoyer un message à ${contact?.firstName} — ${contact?.relationship}`}
                  aria-busy={isMsgLoading}
                  disabled={isDisabled || isCallLoading}
                  className="interactive-btn"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    background: isMsgLoading
                      ? 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)'
                      : 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    borderRadius: '12px',
                    padding: 'clamp(8px, 1vh, 14px) clamp(10px, 1.2vw, 16px)',
                    minWidth: 'clamp(80px, 8vw, 100px)',
                    minHeight: '64px',
                    boxShadow: isMsgLoading ? 'none' : '0 3px 12px rgba(59,130,246,0.35)',
                    transition: 'all 0.15s ease',
                    border: 'none',
                    cursor: (isDisabled || isCallLoading) ? 'not-allowed' : 'pointer',
                    opacity: (isDisabled || isCallLoading) ? 0.6 : 1,
                  }}>
                  {isMsgLoading ? (
                    <>
                      <div style={{
                        width: '22px', height: '22px', borderRadius: '50%',
                        border: '3px solid rgba(255,255,255,0.8)',
                        borderTopColor: 'transparent',
                        animation: 'sab-spin 0.7s linear infinite',
                      }} aria-hidden="true" />
                      <span style={{ fontSize: 'clamp(12px, 1.2vw, 14px)', fontWeight: 700, color: 'white', whiteSpace: 'nowrap', marginTop: '2px' }}>
                        Ouverture…
                      </span>
                    </>
                  ) : (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      <span style={{ fontSize: 'clamp(14px, 1.4vw, 17px)', fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
                        Message
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <style>{`@keyframes sab-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
